import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { apiCall } from '../restaurant/RestaurantBackend';
import { getAuthToken } from '../../services/authToken';
import TenantSelector from '../common/TenantSelector';
import { useTenant } from '../../contexts/TenantContext';

// ---------------------------------------------------------------------------
// Types — match the shapes returned by the backend.
// ---------------------------------------------------------------------------

interface ServerSocketInfo {
  socket_id: string;
  client_host: string;
  client_port: number;
  connected_at: string;
  server_id: string;
  device_id: string | null;
}

interface ActiveServer {
  id: string;
  name: string;
  tenant_id?: string | null;
  location_id?: string | null;
}

interface DeviceTelemetry {
  battery_level?: number;
  battery_charging?: boolean;
  storage_free_mb?: number;
  audio_level_avg?: number;
  ws_reconnect_count?: number;
  uptime_seconds?: number;
}

interface SocketStatusResponse {
  total_active_servers: number;
  total_server_sockets: number;
  total_admin_sockets: number;
  server_sockets: Record<string, ServerSocketInfo>;
  active_servers: ActiveServer[];
  device_telemetry: Record<string, DeviceTelemetry>;
}

interface DeviceShiftEntry {
  shift_id: string;
  server_id: string | null;
  server_name: string | null;
  started_at: string | null;
  ended_at: string | null;
  shift_type: string | null;
  role: string | null;
  last_activity_at: string | null;
  transcript_part_count: number;
  interaction_count: number;
}

interface KnownDevice {
  device_id: string;
  shift_count: number;
  last_seen: string | null;
  first_seen: string | null;
  server_names: string | null;
  device_name: string | null;
  app_version: string | null;
  app_build: string | null;
  patch_number: number | null;
  platform: string | null;
  device_model: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OpsView: React.FC = () => {
  const { selectedTenant } = useTenant();
  const [status, setStatus] = useState<SocketStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const [knownDevices, setKnownDevices] = useState<KnownDevice[] | null>(null);
  const [knownDevicesError, setKnownDevicesError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await apiCall('/socket-status');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data: SocketStatusResponse = await resp.json();
      setStatus(data);
      setStatusError(null);
      setLastFetched(new Date());
    } catch (e) {
      Sentry.captureException(e);
      setStatusError(e instanceof Error ? e.message : 'fetch failed');
    }
  }, []);

  const fetchKnownDevices = useCallback(async () => {
    if (!selectedTenant) {
      setKnownDevices(null);
      return;
    }
    try {
      const accessToken = getAuthToken();
      const resp = await apiCall(
        `/api/ops/tenant-devices?tenant_id=${encodeURIComponent(selectedTenant)}`,
        {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setKnownDevices(data.devices ?? []);
      setKnownDevicesError(null);
    } catch (e) {
      Sentry.captureException(e);
      setKnownDevicesError(e instanceof Error ? e.message : 'fetch failed');
    }
  }, [selectedTenant]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  useEffect(() => {
    fetchKnownDevices();
  }, [fetchKnownDevices]);

  // Filter live devices to the currently selected tenant.
  const tenantDevices = useMemo(() => {
    if (!status) return [];
    const serverById = new Map(status.active_servers.map((s) => [s.id, s]));
    return Object.values(status.server_sockets).filter((sock) => {
      const server = serverById.get(sock.server_id);
      if (!server) return false;
      if (!selectedTenant) return true;
      return server.tenant_id === selectedTenant;
    });
  }, [status, selectedTenant]);

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-6xl mx-auto p-6">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-3xl font-display font-semibold text-ink">
              Ops dashboard
            </h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-sm text-inkMuted">Tenant:</span>
              <TenantSelector />
              {lastFetched && (
                <span className="text-xs text-inkMuted">
                  · last refresh {lastFetched.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              fetchStatus();
              fetchKnownDevices();
            }}
            className="inline-flex items-center gap-1 text-sm text-inkMuted hover:text-ink shrink-0"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </header>

        {statusError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            Failed to load socket status: {statusError}
          </div>
        )}

        <Section title="Devices at tenant">
          {(() => {
            const liveByDeviceId = new Map<string, ServerSocketInfo>();
            for (const sock of tenantDevices) {
              if (sock.device_id) liveByDeviceId.set(sock.device_id, sock);
            }

            const merged: MergedDevice[] = [];
            // Start with known devices (covers both live and offline)
            for (const k of knownDevices ?? []) {
              merged.push({
                deviceId: k.device_id,
                live: liveByDeviceId.get(k.device_id) ?? null,
                known: k,
              });
            }
            // Live devices that haven't run a shift yet (no row in known)
            for (const sock of tenantDevices) {
              if (!sock.device_id) {
                merged.push({ deviceId: sock.socket_id, live: sock, known: null });
                continue;
              }
              if (!merged.some((m) => m.deviceId === sock.device_id)) {
                merged.push({ deviceId: sock.device_id, live: sock, known: null });
              }
            }
            // Live first, then offline by last_seen desc
            merged.sort((a, b) => {
              if (!!a.live !== !!b.live) return a.live ? -1 : 1;
              const at = a.known?.last_seen ?? a.live?.connected_at ?? '';
              const bt = b.known?.last_seen ?? b.live?.connected_at ?? '';
              return bt.localeCompare(at);
            });

            if (!status || (!knownDevices && selectedTenant)) {
              return <Skeleton />;
            }
            if (knownDevicesError) {
              return (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                  Failed to load known devices: {knownDevicesError}
                </div>
              );
            }
            if (merged.length === 0) {
              return (
                <Empty
                  text={
                    selectedTenant
                      ? 'No devices have ever run shifts at this tenant.'
                      : 'Select a tenant to see its devices.'
                  }
                />
              );
            }
            return (
              <div className="space-y-3">
                {merged.map((m) => (
                  <DeviceCard
                    key={m.deviceId}
                    merged={m}
                    activeServers={status.active_servers}
                    telemetry={status.device_telemetry}
                  />
                ))}
              </div>
            );
          })()}
        </Section>

        <Section title="Pipeline health">
          <div className="grid grid-cols-2 gap-3">
            <StubTile
              title="Deepgram"
              note="Latency / error-rate instrumentation pending. See Sentry for failures in the meantime."
            />
            <StubTile
              title="Anthropic (Claude)"
              note="Latency / error-rate instrumentation pending. See Sentry for failures in the meantime."
            />
          </div>
        </Section>

        <Section title="Sentry">
          <p className="text-sm text-inkMuted">
            Recent errors aren't proxied through this view. Open Sentry directly,
            filtered to this tenant if you've set up the project filter.
          </p>
          <a
            href="https://bespoke-ai.sentry.io/issues/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm text-coral hover:underline"
          >
            Open Sentry <ExternalLink className="w-3 h-3" />
          </a>
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DeviceCard — one row per device (live or offline), with its recent shifts.
// ---------------------------------------------------------------------------

interface MergedDevice {
  deviceId: string;
  live: ServerSocketInfo | null;
  known: KnownDevice | null;
}

interface DeviceCardProps {
  merged: MergedDevice;
  activeServers: ActiveServer[];
  telemetry: Record<string, DeviceTelemetry>;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ merged, activeServers, telemetry }) => {
  const [shifts, setShifts] = useState<DeviceShiftEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savedName, setSavedName] = useState<string | null>(null);

  const { live, known } = merged;
  const isLive = live != null;
  const liveServer = live ? activeServers.find((s) => s.id === live.server_id) : null;
  const telemetryEntries = isLive ? Object.values(telemetry) : []; // shift-id keyed; not joined to device today
  const deviceIdForFetch = live?.device_id ?? known?.device_id ?? null;
  const displayName = savedName ?? known?.device_name ?? null;

  const saveName = useCallback(async () => {
    if (!deviceIdForFetch) return;
    const trimmed = nameDraft.trim();
    try {
      const accessToken = getAuthToken();
      const resp = await apiCall(
        `/api/ops/devices/${encodeURIComponent(deviceIdForFetch)}/name`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ name: trimmed === '' ? null : trimmed }),
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSavedName(trimmed === '' ? null : trimmed);
      setEditingName(false);
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [deviceIdForFetch, nameDraft]);

  const fetchShifts = useCallback(async () => {
    if (!deviceIdForFetch) {
      setShifts([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const accessToken = getAuthToken();
      const resp = await apiCall(
        `/api/ops/device-shifts?device_id=${encodeURIComponent(deviceIdForFetch)}&limit=10`,
        {
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : undefined,
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setShifts(data.shifts ?? []);
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [deviceIdForFetch]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  // Header label: prefer live current-server, fall back to known servers list
  const headerLabel = liveServer?.name ?? known?.server_names ?? '(unknown server)';

  return (
    <div className="bg-white border border-softBorder rounded-lg shadow-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-display font-semibold text-ink truncate flex items-center gap-2">
            <span className="truncate">{headerLabel}</span>
            <span
              className={`shrink-0 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                isLive
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {isLive ? 'LIVE' : 'OFFLINE'}
            </span>
          </h3>
          {/* Editable display name */}
          <div className="text-xs text-inkMuted mt-0.5 flex items-center gap-2">
            {editingName ? (
              <>
                <input
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  placeholder="e.g. Preston's Phone"
                  maxLength={64}
                  autoFocus
                  className="px-1.5 py-0.5 border border-softBorder rounded text-xs"
                />
                <button onClick={saveName} className="text-coral hover:underline">save</button>
                <button onClick={() => setEditingName(false)} className="text-inkMuted hover:underline">cancel</button>
              </>
            ) : (
              <>
                <span>{displayName ?? <span className="italic text-inkMuted/60">unnamed</span>}</span>
                {deviceIdForFetch && (
                  <button
                    onClick={() => {
                      setNameDraft(displayName ?? '');
                      setEditingName(true);
                    }}
                    className="text-coral hover:underline"
                  >
                    {displayName ? 'rename' : 'name'}
                  </button>
                )}
              </>
            )}
          </div>
          <p className="text-xs text-inkMuted mt-0.5 font-mono">
            device_id: {deviceIdForFetch ?? <span className="text-amber-600">none (pre-rollout build)</span>}
          </p>
          {/* App version + Shorebird patch + device model */}
          {known && (known.app_version || known.device_model) && (
            <p className="text-xs text-inkMuted mt-0.5">
              {known.app_version && (
                <>
                  app v{known.app_version}
                  {known.app_build && <> (build {known.app_build})</>}
                  {known.patch_number != null && <> · patch {known.patch_number}</>}
                </>
              )}
              {known.app_version && known.device_model && ' · '}
              {known.device_model}
            </p>
          )}
          {isLive ? (
            <p className="text-xs text-inkMuted">
              connected {formatRelative(live!.connected_at)} · from {live!.client_host}
            </p>
          ) : (
            <p className="text-xs text-inkMuted">
              last seen {formatRelative(known?.last_seen ?? null)} · {known?.shift_count ?? 0} shifts total
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={fetchShifts}
          className="text-xs text-inkMuted hover:text-ink shrink-0 inline-flex items-center gap-1"
          title="Refresh shifts"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>

      {isLive && telemetryEntries.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-inkMuted">
          {telemetryEntries[0].battery_level !== undefined && (
            <span>🔋 {telemetryEntries[0].battery_level}%</span>
          )}
          {telemetryEntries[0].ws_reconnect_count !== undefined && (
            <span>↻ {telemetryEntries[0].ws_reconnect_count} reconnects</span>
          )}
          {telemetryEntries[0].audio_level_avg !== undefined && (
            <span>RMS {telemetryEntries[0].audio_level_avg.toFixed(3)}</span>
          )}
        </div>
      )}

      <div className="mt-3 border-t border-softBorder pt-3">
        <p className="text-xs font-medium text-inkMuted mb-2">
          Recent shifts on this device
        </p>
        {loading && <p className="text-xs text-inkMuted">Loading…</p>}
        {error && <p className="text-xs text-red-600">Failed: {error}</p>}
        {shifts && shifts.length === 0 && !loading && (
          <p className="text-xs text-inkMuted">No shifts yet.</p>
        )}
        {shifts && shifts.length > 0 && (
          <div className="space-y-1">
            {shifts.map((s) => (
              <div
                key={s.shift_id}
                className="flex items-center justify-between gap-2 text-xs py-1 border-b border-softBorder last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-ink">
                    {s.server_name ?? '(unknown)'}
                  </span>
                  <span className="text-inkMuted ml-2">
                    {formatShort(s.started_at)}
                    {s.ended_at && ` → ${formatShort(s.ended_at)}`}
                    {!s.ended_at && (
                      <span className="text-emerald-600 font-medium ml-1">
                        ACTIVE
                      </span>
                    )}
                  </span>
                </div>
                <div className="text-inkMuted shrink-0">
                  {s.transcript_part_count} parts · {s.interaction_count} interactions
                </div>
                {/* feat-274 AC5. `last_activity_at` has been on this wire since
                    routes/ops.py:146 — the column was built for exactly this
                    failure mode and no JSX ever read it. Named "last speech"
                    rather than "last activity" because it is
                    MAX(transcript_part.created): admin triage of a red row on
                    the Live Servers roster lands here, and the gap between when
                    a device last sent AUDIO and when its server last SPOKE is
                    the diagnosis. Labelling it generically is how that gap gets
                    misread. */}
                <div className="text-inkMuted shrink-0 whitespace-nowrap">
                  last speech {formatRelativeUtc(s.last_activity_at)}
                </div>
                {s.server_id && (
                  <a
                    href={`/restaurant/shifts/${s.shift_id}/transcript/${s.server_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-inkMuted hover:text-coral p-1 -m-1"
                    title="Open transcript in new tab"
                    aria-label="Open transcript in new tab"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small UI bits
// ---------------------------------------------------------------------------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-6">
    <h2 className="text-sm font-semibold text-inkMuted uppercase tracking-wide mb-2">
      {title}
    </h2>
    {children}
  </section>
);

const StubTile: React.FC<{ title: string; note: string }> = ({ title, note }) => (
  <div className="bg-white border border-dashed border-softBorder rounded-lg p-4">
    <h3 className="text-base font-display font-semibold text-ink">{title}</h3>
    <p className="text-xs text-inkMuted mt-1">{note}</p>
    <p className="text-xs text-amber-600 mt-2">stub — instrumentation pending</p>
  </div>
);

const Skeleton = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="h-20 bg-white border border-softBorder rounded-lg animate-pulse"
      />
    ))}
  </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="bg-white border border-dashed border-softBorder rounded-lg p-4 text-sm text-inkMuted text-center">
    {text}
  </div>
);

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso);
  const d = Date.now() - t.getTime();
  const sec = Math.round(d / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

/**
 * `formatRelative` for a timestamp this route emits WITHOUT a zone suffix.
 *
 * `routes/ops.py` emits zone-less ISO while `live-servers` emits explicit `Z`,
 * and `new Date('2026-09-09T14:23:00')` is parsed as LOCAL time. In PDT that is
 * an eight-hour error, and it would land here as "8h ago" on a perfectly healthy
 * device — i.e. it manufactures the exact false positive this feature exists to
 * prevent. `last_activity_at` is MAX(transcript_part.created), which F6 confirms
 * is stored in UTC, so a bare string is normalised to UTC before parsing.
 *
 * The other timestamps on this card (`started_at` / `ended_at`, via
 * `formatShort`) come from `shift`, which F6 says is written in the LOCATION's
 * local timezone. That mismatch is pre-existing, is its own feature, and is
 * deliberately not touched here — which is precisely why this helper is separate
 * from `formatRelative` rather than a fix applied to it.
 */
function formatRelativeUtc(iso: string | null): string {
  if (!iso) return '—';
  const hasZone = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso);
  return formatRelative(hasZone ? iso : `${iso}Z`);
}

function formatShort(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso);
  return t.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default OpsView;
